'use client';

import {
  createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo,
} from 'react';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';
import type { McKeywordSource } from '@components/features/candidate-jobs-sidebar/cardTypes';

export type CandidateSearchFilters = {
  keyword?: string;
  locations?: string[];
  salaryMin?: number;
  salaryMax?: number;
};

export type SidebarView =
  | 'new-search'
  | 'new-job-search-form'
  | 'my-bestie-landing'
  | 'browse-all'
  | { keyword: string; source: McKeywordSource }
  | { candidateSearch: CandidateSearchFilters }
  | { editSearch: CandidateSearchFilters };

interface SidebarNavigationContextValue {
  currentView: SidebarView;
  setView: (view: SidebarView) => void;
  navigateToNewSearch: () => void;
  navigateToNewJobSearchForm: () => void;
  navigateToMyBestieLanding: () => void;
  navigateToBrowseAll: () => void;
  navigateToKeywordSearch: (keyword: string, source: McKeywordSource) => void;
  navigateToCandidateSearch: (filters: CandidateSearchFilters) => void;
  navigateToEditSearch: (filters: CandidateSearchFilters) => void;
}

const SidebarNavigationContext = createContext<SidebarNavigationContextValue | undefined>(undefined);

interface SidebarNavigationProviderProps {
  children: ReactNode;
  initialView?: SidebarView;
  locale: Locale;
}

/** Derives the sidebar view from a path + search string (used for popstate/back-forward). */
const getViewFromUrl = (path: string, search: string): SidebarView => {
  const urlParams = new URLSearchParams(search);
  const isEditMode = urlParams.get('edit') === '1';

  // Backward compat: old keyword-search URL — treat as candidateSearch
  const keywordSearchMatch = path.match(/\/mc\/job-search\/([^/]+)/);
  if (keywordSearchMatch) {
    const keyword = decodeURIComponent(keywordSearchMatch[1]).replace(/-/g, ' ');
    return { candidateSearch: { keyword } };
  }

  // Candidate search/edit results: /mc/locuri-de-munca[-in-CITY][/KEYWORD] or /mc/jobs[-in-CITY][/KEYWORD]
  const candidateListMatch = path.match(/\/mc\/(?:locuri-de-munca|jobs)(?:-in-([^/]+?))?(?:\/([^/?]+))?(?=[/?]|$)/);
  if (candidateListMatch) {
    const locationSlug = candidateListMatch[1];
    const keywordSlug = candidateListMatch[2];
    const keyword = keywordSlug ? decodeURIComponent(keywordSlug).replace(/-/g, ' ') : undefined;
    const locations = locationSlug ? locationSlug.split(',').map((s) => decodeURIComponent(s)) : undefined;
    const salaryMin = urlParams.get('salaryMin') ? Number(urlParams.get('salaryMin')) : undefined;
    const salaryMax = urlParams.get('salaryMax') ? Number(urlParams.get('salaryMax')) : undefined;
    const filters = {
      ...(keyword && { keyword }),
      ...(locations?.length && { locations }),
      ...(salaryMin && { salaryMin }),
      ...(salaryMax && { salaryMax }),
    };
    // ?edit=1 signals the edit form should be shown, not results
    return isEditMode ? { editSearch: filters } : { candidateSearch: filters };
  }

  if (/\/mc\/my-bestie/.test(path)) {
    return 'my-bestie-landing';
  }

  if (/\/mc\/search/.test(path)) {
    const keyword = urlParams.get('keyword') ?? undefined;
    const locations = urlParams.getAll('location');
    const salaryMin = urlParams.get('salaryMin') ? Number(urlParams.get('salaryMin')) : undefined;
    const salaryMax = urlParams.get('salaryMax') ? Number(urlParams.get('salaryMax')) : undefined;
    if (keyword || locations.length || salaryMin) {
      return {
        editSearch: {
          ...(keyword && { keyword }),
          ...(locations.length && { locations }),
          ...(salaryMin && { salaryMin }),
          ...(salaryMax && { salaryMax }),
        },
      };
    }
    return 'new-job-search-form';
  }

  return 'new-search';
};

/**
 * @description Provider for sidebar navigation state management.
 *
 * View state is initialised from the `initialView` prop (set by the server page component
 * based on the current URL). Client-side navigation calls `setView` which updates state
 * immediately and then pushes the matching URL. Browser back/forward is handled via a
 * `popstate` listener so the effect cannot race with explicit `setView` calls.
 */
export const SidebarNavigationProvider = (props: SidebarNavigationProviderProps) => {
  const { children, initialView = 'new-search', locale } = props;

  const [currentView, setCurrentView] = useState<SidebarView>(initialView);

  // Update URL using pushState — avoids a Next.js navigation that would remount the component tree.
  // Hard reloads of any MC URL still get the correct initialView from the server page component.
  // Browser back/forward is handled separately via the popstate listener.
  const updateURL = useCallback((view: SidebarView) => {
    if (view === 'new-search' || view === 'browse-all') {
      window.history.pushState({}, '', getI18nLinkHref('/mc', locale));
    } else if (view === 'my-bestie-landing') {
      window.history.pushState({}, '', getI18nLinkHref('/mc/my-bestie', locale));
    } else if (view === 'new-job-search-form') {
      window.history.pushState({}, '', getI18nLinkHref('/mc/search', locale));
    } else if ('keyword' in view) {
      const slug = slugify(view.keyword);
      window.history.pushState({}, '', getI18nLinkHref(`/mc/job/list/${slug}`, locale));
    } else if ('editSearch' in view) {
      const {
        keyword, locations, salaryMin, salaryMax,
      } = view.editSearch;
      const keywordSlug = keyword ? `/${slugify(keyword)}` : '';

      let base: string;
      if (locations?.length) {
        const locationSlug = slugify(locations.join(','));
        base = getI18nLinkHref(`/mc/job/list/location-${locationSlug}`, locale).replace(/%2C/g, ',');
      } else {
        base = getI18nLinkHref('/mc/job/list', locale);
      }

      const params = new URLSearchParams();
      params.set('edit', '1');
      if (salaryMin) params.set('salaryMin', String(salaryMin));
      if (salaryMax) params.set('salaryMax', String(salaryMax));
      const qs = params.toString();
      window.history.pushState({}, '', `${base}${keywordSlug}?${qs}`);
    } else if ('candidateSearch' in view) {
      const {
        keyword, locations, salaryMin, salaryMax,
      } = view.candidateSearch;
      const keywordSlug = keyword ? `/${slugify(keyword)}` : '';

      let base: string;
      if (locations?.length) {
        const locationSlug = slugify(locations.join(','));
        base = getI18nLinkHref(`/mc/job/list/location-${locationSlug}`, locale).replace(/%2C/g, ',');
      } else {
        base = getI18nLinkHref('/mc/job/list', locale);
      }

      const params = new URLSearchParams();
      if (salaryMin) params.set('salaryMin', String(salaryMin));
      if (salaryMax) params.set('salaryMax', String(salaryMax));
      const qs = params.toString();
      window.history.pushState({}, '', `${base}${keywordSlug}${qs ? `?${qs}` : ''}`);
    }
  }, [locale]);

  // Set view and update URL — state updates immediately, URL follows asynchronously.
  const setView = useCallback((view: SidebarView) => {
    setCurrentView(view);
    updateURL(view);
  }, [updateURL]);

  // Sync view on browser back/forward navigation (popstate only — never races with setView).
  useEffect(() => {
    const handlePopState = () => {
      setCurrentView(getViewFromUrl(window.location.pathname, window.location.search));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Convenience methods
  const navigateToNewSearch = useCallback(() => setView('new-search'), [setView]);
  const navigateToNewJobSearchForm = useCallback(() => setView('new-job-search-form'), [setView]);
  const navigateToMyBestieLanding = useCallback(() => setView('my-bestie-landing'), [setView]);
  const navigateToBrowseAll = useCallback(() => setView('browse-all'), [setView]);
  const navigateToKeywordSearch = useCallback((keyword: string, source: McKeywordSource) => setView({ keyword, source }), [setView]);
  const navigateToCandidateSearch = useCallback((filters: CandidateSearchFilters) => setView({ candidateSearch: filters }), [setView]);
  const navigateToEditSearch = useCallback((filters: CandidateSearchFilters) => setView({ editSearch: filters }), [setView]);

  const value: SidebarNavigationContextValue = useMemo(() => ({
    currentView,
    setView,
    navigateToNewSearch,
    navigateToNewJobSearchForm,
    navigateToMyBestieLanding,
    navigateToBrowseAll,
    navigateToKeywordSearch,
    navigateToCandidateSearch,
    navigateToEditSearch,
  }), [
    currentView, setView, navigateToNewSearch, navigateToNewJobSearchForm,
    navigateToMyBestieLanding, navigateToBrowseAll,
    navigateToKeywordSearch, navigateToCandidateSearch, navigateToEditSearch,
  ]);

  return (
    <SidebarNavigationContext.Provider value={value}>
      {children}
    </SidebarNavigationContext.Provider>
  );
};

/**
 * @description Hook to access sidebar navigation context.
 * Throws error if used outside of SidebarNavigationProvider.
 */
export const useSidebarNavigation = () => {
  const context = useContext(SidebarNavigationContext);

  if (!context) {
    throw new Error('useSidebarNavigation must be used within SidebarNavigationProvider');
  }

  return context;
};

